import { cn } from '../../lib/utils';

interface CountBadgeProps {
  /**
   * The integer count to render. `0` is allowed and styled muted
   * (the badge stays visible so empty buckets aren't invisible).
   * Negative values render as `0` defensively.
   */
  readonly value: number;
  /** Optional extra classes (positioning, margin) from the caller. */
  readonly className?: string;
}

/**
 * Compact circular/pill count badge used in filter dropdowns to
 * surface "how many results would this option produce?" inline with
 * each option (Maria 2026-06-05 ask).
 *
 * Visual contract:
 *   - Perfect 22×22 circle when a single digit is rendered.
 *   - Grows horizontally for 2+ digits via `min-w-[22px]` + `px-1.5`
 *     so the height stays constant and the rounded-full corners
 *     resolve to an even pill.
 *   - Soft gray fill (`bg-text-4/10`); navy text on non-zero.
 *   - `0` renders as muted gray text so "no results" reads at a
 *     glance without dropping the badge entirely. The option row
 *     remains clickable — picking a zero-count filter narrows the
 *     table to its empty state, matching the page's existing UX.
 *   - `tabular-nums` so digits in adjacent rows align vertically
 *     when several options share a width (a column of right-edge
 *     badges scans as a list of numbers).
 *
 * Pure presentational. Has no interactive behaviour of its own —
 * callers wrap the parent option row with a button / role=option
 * for the clickability.
 */
export function CountBadge({ value, className }: CountBadgeProps) {
  const display = Math.max(0, Math.trunc(value));
  const isZero = display === 0;
  return (
    <span
      aria-label={`${display} ${display === 1 ? 'result' : 'results'}`}
      className={cn(
        'inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5',
        'bg-text-4/10 text-[11px] font-semibold leading-none tabular-nums',
        isZero ? 'text-text-4' : 'text-navy',
        className,
      )}
    >
      {display}
    </span>
  );
}
